/**
 * El candado de una función de pago.
 *
 * Nació para Analíticas y ahora lo comparten varias pantallas, así que lo único
 * que cambia —qué función es y qué se lleva quien pague— viaja como dato en vez
 * de estar escrito dentro. Las clases de CSS siguen llamándose `analytics`
 * porque describen el candado, no la sección: renombrarlas movería medio archivo
 * de estilos sin cambiar un píxel.
 *
 * La regla que no se negocia: quien no paga NUNCA recibe datos reales. Se le
 * pintan datos inventados detrás del desenfoque, para que ni las herramientas de
 * desarrollo enseñen cifras que no le corresponden.
 */
import type { CSSProperties, ReactNode } from 'react'
import { IconArrowUpRight, IconCheck, IconLock } from '@tabler/icons-react'
import { StripeMark } from './sections/StripeMark'
import { useI18n } from '../../lib/i18n'
import { MASCOT_WAVE_CENTER, MASCOT_WAVE_SIZES, MascotOutlinePattern } from '../MascotOutlinePattern'

/**
 * Lo que distingue a una función de otra. El resto del candado es idéntico.
 *
 * Son CLAVES del catálogo, no texto. Estas descripciones viven en constantes de
 * módulo, y una constante no puede llamar a un hook: guardando el texto ya
 * escrito, el candado seguiría en español en un panel en inglés y nada lo
 * diría. Guardando la clave, quien la pinta —que sí es un componente— la
 * traduce. Los nombres llevan `Key` a propósito: renombrarlos es lo que hizo
 * que el compilador señalara todos los sitios que había que tocar.
 */
export type UpsellCopy = {
  saleTitleKey: string
  saleDescriptionKey: string
  salePointKeys: string[]
  /** El renglón bajo el botón. Por omisión, en qué planes entra. */
  noteKey?: string
  /**
   * La marca de Stripe junto a ese renglón.
   *
   * Un dato y no un componente: estas constantes son claves de catálogo a
   * propósito —viven fuera de un componente y no pueden llamar a un hook—, y
   * meterles JSX abriría la puerta a que acabaran guardando texto ya traducido.
   *
   * Va abajo y pequeña por lo que se está vendiendo: quien no puede cobrar no
   * tiene un problema con Stripe, tiene un plan que no llega. Stripe es cómo se
   * hace, no qué se ofrece.
   */
  stripeNote?: boolean
}

export const ANALYTICS_UPSELL: UpsellCopy = {
  saleTitleKey: 'upsell.analytics.saleTitle',
  saleDescriptionKey: 'upsell.analytics.saleDescription',
  salePointKeys: [
    'upsell.analytics.point1',
    'upsell.analytics.point2',
    'upsell.analytics.point3',
    'upsell.analytics.point4',
  ],
}

/**
 * Cobrar por el sitio.
 *
 * Sale donde antes había un aviso que empezaba por «conectar Stripe»: el
 * trámite por delante de lo que se gana con él, y con cara de gestión pendiente
 * en vez de oferta. Lo que se vende es vender; Stripe es la fontanería y va
 * abajo, pequeña, para que quien la conozca la reconozca.
 */
export const PAYMENTS_UPSELL: UpsellCopy = {
  saleTitleKey: 'upsell.payments.saleTitle',
  saleDescriptionKey: 'upsell.payments.saleDescription',
  salePointKeys: [
    'upsell.payments.point1',
    'upsell.payments.point2',
    'upsell.payments.point3',
    'upsell.payments.point4',
  ],
  stripeNote: true,
}

export const ORDERS_UPSELL: UpsellCopy = {
  saleTitleKey: 'upsell.orders.saleTitle',
  saleDescriptionKey: 'upsell.orders.saleDescription',
  salePointKeys: [
    'upsell.orders.point1',
    'upsell.orders.point2',
    'upsell.orders.point3',
    'upsell.orders.point4',
  ],
}

/**
 * La tarjeta de venta, suelta.
 *
 * Vivía dentro del candado de Analíticas, que además difumina lo que hay
 * detrás. Pero hay pantallas donde no hay nada que difuminar —Cobros, Depósitos,
 * Ventas sin una sola venta— y lo único que falta es la misma oferta. Separarla
 * es lo que evita una segunda tarjeta parecida que a los dos meses ya no lo es.
 */
export function UpsellCard({ copy, eyebrow, title, description, points, cta, note, onOpenPlans, dismiss, standalone = false, slim = false, style }: {
  /** Lo que se vende, en claves. Ausente cuando los textos vienen ya escritos. */
  copy?: UpsellCopy
  onOpenPlans: () => void
  /**
   * Quitar la tarjeta: «Cerrar» por ahora, «No volver a mostrar» para siempre.
   *
   * Dos botones de texto bajo la frase, a la izquierda, y no una cruz en la
   * esquina: la cruz caía encima del botón blanco de «Ver planes» en la versión
   * corta, justo donde se pulsa. Y dos y no uno porque son dos respuestas
   * distintas —«ahora no» y «ya lo sé»—, y obligar a elegir la definitiva para
   * quitarla un rato era castigar a quien sólo quería ver la lista.
   *
   * Sólo donde la oferta va ENCIMA del trabajo de la pantalla —la lista de
   * ofertas—. Ausente, no se puede quitar: sobre una pantalla difuminada, o en
   * el paso del asistente donde se pone precio, la tarjeta es la explicación de
   * por qué algo no se puede. Quien llama decide y recuerda; la tarjeta sólo
   * pinta los botones.
   */
  dismiss?: { onClose: () => void; onNeverShow: () => void } | undefined
  /**
   * Encima del trabajo de la pantalla, no en lugar de él.
   *
   * En Cobros la oferta va sobre la lista de ofertas, que es a lo que se viene:
   * a esa altura, la tarjeta entera empujaba el catálogo por debajo del pliegue.
   * Se queda con el titular, la frase y el botón —la oferta completa— y suelta
   * las pastillas, que son el detalle de alguien que ya se paró a leer.
   */
  /**
   * Textos ya traducidos, para cuando no pueden vivir en una constante.
   *
   * `copy` son claves, y una clave no sabe decir «tu plan Starter» ni «desde
   * 9,50 $/mes»: eso depende del plan de quien mira y se resuelve en tiempo de
   * pintado. La ventana del tope los pasa por aquí; quien vende una función
   * sigue pasando su `copy` y no toca nada. Uno de los dos hace falta.
   */
  /** El rótulo sobre el titular. Por omisión, «planes de pago». */
  eyebrow?: string
  title?: string
  description?: string
  points?: string[]
  cta?: string
  /** El renglón bajo el botón: en el tope, el precio del plan siguiente. */
  note?: string
  slim?: boolean
  /**
   * Sin nada difuminado debajo. Quita el marco de cristal —que sólo tiene
   * sentido sobre algo que se ve borroso a través de él— y la deja ocupar el
   * ancho de la sección, como una tarjeta más de la pantalla.
   */
  standalone?: boolean
  style?: CSSProperties | undefined
}) {
  const { t } = useI18n()
  return (
    <div
      className={`dash-analytics-upsell-card${standalone ? ' is-standalone' : ''}${slim ? ' is-slim' : ''}`}
      {...(style ? { style } : {})}
    >
      {/* Sin ojos y sin los anillos pequeños: el centro del patrón cae detrás
          del botón, y allí la mascota se leía como un dibujo escondido debajo
          de la acción. Queda la onda entrando por la derecha. */}
      <MascotOutlinePattern className="dash-analytics-upsell-pattern" eyes={false} sizes={MASCOT_WAVE_SIZES} center={MASCOT_WAVE_CENTER} />
      {/* Texto a un lado y acción al otro, como la oferta de Colaboradores.
          En una sola columna el botón caía al final de una lista de cuatro
          pastillas y la tarjeta se leía como un formulario largo; enfrente
          del texto se ve lo que se ofrece y lo que hay que pulsar a la vez. */}
      <div className="dash-analytics-upsell-copy">
        {/* El candado y el rótulo, encima del titular: dicen de un vistazo por
            qué lo de detrás está borroso, antes de que haya que leer nada.
            En corto sobran los dos: ahí no hay nada difuminado que explicar
            —debajo está el catálogo, que se puede tocar— y eran dos renglones
            de preámbulo antes de llegar a lo que se ofrece. */}
        {slim ? null : (
          <>
            <span className="dash-upsell-lock" aria-hidden="true"><IconLock size={16} /></span>
            <span className="bento-eyebrow">{eyebrow ?? t('upsell.eyebrow')}</span>
          </>
        )}
        <h3>{title ?? t(copy!.saleTitleKey)}</h3>
        <p>{description ?? t(copy!.saleDescriptionKey)}</p>
        {slim ? null : (
          <ul className="dash-analytics-upsell-points">
            {(points ?? copy?.salePointKeys.map((key) => t(key)) ?? []).map((point) => (
              <li key={point}><IconCheck size={13} aria-hidden="true" /> {point}</li>
            ))}
          </ul>
        )}
        {dismiss ? (
          <div className="dash-upsell-dismiss-row">
            <button type="button" onClick={dismiss.onClose}>{t('common.close')}</button>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={dismiss.onNeverShow}>{t('upsell.dismiss')}</button>
          </div>
        ) : null}
      </div>
      <div className="dash-analytics-upsell-action">
        <button
          type="button"
          className="dash-primary-btn dash-analytics-upsell-cta"
          onClick={onOpenPlans}
        >
          {cta ?? t('upsell.seePlans')} <IconArrowUpRight size={15} aria-hidden="true" />
        </button>
        {/* Dos renglones y no uno con un punto medio: son dos cosas distintas
            —con qué se cobra y qué plan hace falta— y juntas se leían como una
            sola condición larga.
            En corto no van: la tarjeta ahí es un renglón sobre el catálogo, y
            la letra pequeña la doblaba de alto para decir lo que la pantalla de
            planes explica al pulsar. */}
        {slim ? null : (
        <div className="dash-analytics-upsell-notes">
          {copy?.stripeNote ? (
            <p className="dash-analytics-upsell-note">
              <StripeMark size={13} /> {t('upsell.stripeNote')}
            </p>
          ) : null}
          <p className="dash-analytics-upsell-note">{note ?? t(copy?.noteKey ?? 'upsell.availableIn')}</p>
        </div>
        )}
      </div>
    </div>
  )
}

/**
 * Envuelve una pantalla de pago —alimentada con datos FALSOS— en una capa que
 * desenfoca sus cifras sin ablandar las tarjetas de alrededor, con la oferta
 * centrada encima. `variant="full"` la ancla a media altura en la vista larga
 * de Analíticas.
 */
export function FeatureUpsell({ onOpenPlans, children, variant = 'compact', copy = ANALYTICS_UPSELL }: {
  onOpenPlans: () => void
  children: ReactNode
  variant?: 'compact' | 'full'
  copy?: UpsellCopy
}) {
  const overlayAlign: CSSProperties = variant === 'full'
    ? { alignItems: 'flex-start' }
    : { alignItems: 'center' }
  const cardPosition: CSSProperties = variant === 'full'
    ? { position: 'sticky', top: '50%', transform: 'translateY(-50%)', zIndex: 51 }
    : {}
  return (
    <div style={{ position: 'relative' }}>
      <div className="dash-analytics-upsell-preview" aria-hidden="true">
        {children}
      </div>
      <div style={{ position: 'absolute', inset: 0, zIndex: 50, display: 'flex', justifyContent: 'center', ...overlayAlign }}>
        {/*
          * La venta, de una vez y no detrás de «Más información».
          *
          * Antes aquí había un candado con dos frases y un botón que abría un
          * diálogo con la oferta de verdad. Dos pasos para lo mismo: quien ya
          * está mirando la pantalla difuminada sabe que no la tiene, y el
          * candado sólo repetía eso antes de dejarle ver por qué le conviene.
          * Ahora lo que hay debajo del cristal se explica en el sitio.
          */}
        <UpsellCard copy={copy} onOpenPlans={onOpenPlans} style={cardPosition} />
      </div>
    </div>
  )
}
